import React, { useState, useEffect } from 'react';
import { accountingApi } from '../../../services/accountingApi';

const fmt = (n) => `$${(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const REPORT_CARDS = [
  { id: 'trial-balance',       title: 'Trial Balance',                               description: 'A trial balance displays summary of all ledger balances, and helps in checking whether the transactions are correct and balanced.' },
  { id: 'ledger-report',        title: 'Ledger Report',                                description: 'The ledger report contains the classified and detailed information of all the individual accounts including the debit and credit aspects.' },
  { id: 'balance-sheet',        title: 'Balance Sheet',                                description: 'This report gives you an immediate status of your accounts at a specified date. You can call it a "Snapshot" view of the current position (day) of the financial year.' },
  { id: 'ar-ageing',            title: 'Account Receivable Ageing Report (Summary)',   description: 'This report shows summary of all the sales pending invoices in mentioned days range as per the due date.' },
  { id: 'ap-ageing',            title: 'Account Payable Ageing Report (Summary)',      description: 'This report shows summary of all the purchase pending invoices in mentioned days range as per the due date.' },
  { id: 'ar-ageing-details',    title: 'Account Receivable Ageing Details (Details)',  description: 'This report shows details of all the sales pending invoices in mentioned days range as per the due date.' },
  { id: 'ap-ageing-details',    title: 'Account Payable Ageing Details (Details)',     description: 'This report shows details of all the purchase pending invoices in mentioned days range as per the due date.' },
];

/* ─── Loading / Error helpers ─────────────────────────── */
const Loader = () => (
  <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading...</div>
);
const Err = ({ msg }) => (
  <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>Error: {msg}</div>
);
const Empty = ({ label }) => (
  <tr><td colSpan={10}>
    <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
      <div style={{ fontSize: '2rem' }}>📊</div>
      <div style={{ fontWeight: 600, marginTop: '0.5rem' }}>No {label} data available</div>
    </div>
  </td></tr>
);

/* ─── Trial Balance ───────────────────────────────────── */
const TrialBalanceView = () => {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    accountingApi.getTrialBalance()
      .then(res => setRows(res.data || res || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (error)   return <Err msg={error} />;

  const totalDebit  = rows.reduce((s, r) => s + (parseFloat(r.debit_balance ?? r.debit)  || 0), 0);
  const totalCredit = rows.reduce((s, r) => s + (parseFloat(r.credit_balance ?? r.credit) || 0), 0);

  return (
    <div className="acc-table-wrapper">
      <table className="acc-table">
        <thead>
          <tr>
            <th>Account</th>
            <th className="text-right">Debit</th>
            <th className="text-right">Credit</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? <Empty label="trial balance" /> : rows.map((r, i) => {
            const deb  = parseFloat(r.debit_balance  ?? r.debit)  || 0;
            const cred = parseFloat(r.credit_balance ?? r.credit) || 0;
            return (
              <tr key={i}>
                <td>{r.name || r.account || r.account_name || '—'}</td>
                <td className="text-right" style={{ color: deb ? '#10b981' : '#94a3b8' }}>
                  {deb ? fmt(deb) : '—'}
                </td>
                <td className="text-right" style={{ color: cred ? '#ef4444' : '#94a3b8' }}>
                  {cred ? fmt(cred) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
        {rows.length > 0 && (
          <tfoot>
            <tr style={{ fontWeight: 700, background: 'var(--etpos-bg,#f8fafc)' }}>
              <td>Total</td>
              <td className="text-right" style={{ color: '#10b981' }}>{fmt(totalDebit)}</td>
              <td className="text-right" style={{ color: '#ef4444' }}>{fmt(totalCredit)}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

/* ─── Balance Sheet ───────────────────────────────────── */
const BalanceSheetView = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    accountingApi.getBalanceSheet()
      .then(res => setData(res.data || res))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (error)   return <Err msg={error} />;
  if (!data)   return <Err msg="No data returned" />;

  const getItems = (sec) => {
    if (!sec) return [];
    if (Array.isArray(sec.data)) return sec.data;
    if (Array.isArray(sec)) return sec;
    return [];
  };

  const getTotal = (sec) => {
    if (!sec) return 0;
    if (typeof sec.total !== 'undefined') return parseFloat(sec.total) || 0;
    const items = getItems(sec);
    return items.reduce((s, r) => s + (parseFloat(r.balance || r.amount) || 0), 0);
  };

  const sections = [
    { key: 'assets',      title: 'Assets',      items: getItems(data.assets),      total: getTotal(data.assets) },
    { key: 'liabilities', title: 'Liabilities', items: getItems(data.liabilities), total: getTotal(data.liabilities) },
    { key: 'equity',      title: 'Equity',      items: getItems(data.equity),      total: getTotal(data.equity) },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: '1rem' }}>
      {sections.map(sec => (
        <div key={sec.key} className="acc-card">
          <h3 className="acc-card-title">{sec.title}</h3>
          <table className="acc-table">
            <tbody>
              {sec.items.length === 0 ? (
                <tr><td colSpan={2} style={{ color: '#94a3b8', textAlign: 'center', padding: '1rem' }}>No data</td></tr>
              ) : sec.items.map((r, i) => (
                <tr key={i} style={r.total || r.is_total ? { fontWeight: 700, background: 'var(--etpos-bg,#f8fafc)' } : {}}>
                  <td style={{ paddingLeft: r.total || r.is_total ? '1rem' : '1.75rem' }}>
                    {r.name || r.label || r.account || '—'}
                  </td>
                  <td className="text-right">{fmt(r.balance ?? r.amount ?? 0)}</td>
                </tr>
              ))}
            </tbody>
            {sec.items.length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 700, background: 'var(--etpos-bg,#f8fafc)' }}>
                  <td>Total {sec.title}</td>
                  <td className="text-right">{fmt(sec.total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ))}
    </div>
  );
};

/* ─── AR Ageing ───────────────────────────────────────── */
const ArAgeingView = () => {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    accountingApi.getArAgeing()
      .then(res => setRows(res.data || res || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (error)   return <Err msg={error} />;

  return (
    <div className="acc-table-wrapper">
      <table className="acc-table">
        <thead>
          <tr>
            <th>Customer</th>
            <th className="text-right">Current</th>
            <th className="text-right">1–30 Days</th>
            <th className="text-right">31–60 Days</th>
            <th className="text-right">61–90 Days</th>
            <th className="text-right">90+ Days</th>
            <th className="text-right">Total Due</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? <Empty label="AR ageing" /> : rows.map((r, i) => {
            const current = parseFloat(r.current || r.current_amount || 0);
            const d30     = parseFloat(r['1_30_days'] || r.days_30 || r['1_30'] || r.days30 || 0);
            const d60     = parseFloat(r['31_60_days'] || r.days_60 || r['31_60'] || r.days60 || 0);
            const d90     = parseFloat(r['61_90_days'] || r.days_90 || r['61_90'] || r.days90 || 0);
            const over90  = parseFloat(r['over_90_days'] || r.over_90 || r['90_plus'] || r.over90 || 0);
            const total   = parseFloat(r.total_due) || (current + d30 + d60 + d90 + over90);
            return (
              <tr key={i}>
                <td style={{ fontWeight: 600 }}>{r.name || r.customer || r.contact_name || '—'}</td>
                <td className="text-right">{fmt(current)}</td>
                <td className="text-right">{fmt(d30)}</td>
                <td className="text-right">{fmt(d60)}</td>
                <td className="text-right">{fmt(d90)}</td>
                <td className="text-right" style={{ color: over90 ? '#ef4444' : '#94a3b8' }}>{fmt(over90)}</td>
                <td className="text-right" style={{ fontWeight: 700 }}>{fmt(total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* ─── AP Ageing ───────────────────────────────────────── */
const ApAgeingView = () => {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    accountingApi.getApAgeing()
      .then(res => setRows(res.data || res || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (error)   return <Err msg={error} />;

  return (
    <div className="acc-table-wrapper">
      <table className="acc-table">
        <thead>
          <tr>
            <th>Supplier</th>
            <th className="text-right">Current</th>
            <th className="text-right">1–30 Days</th>
            <th className="text-right">31–60 Days</th>
            <th className="text-right">61–90 Days</th>
            <th className="text-right">90+ Days</th>
            <th className="text-right">Total Due</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? <Empty label="AP ageing" /> : rows.map((r, i) => {
            const current = parseFloat(r.current || r.current_amount || 0);
            const d30     = parseFloat(r['1_30_days'] || r.days_30 || r['1_30'] || r.days30 || 0);
            const d60     = parseFloat(r['31_60_days'] || r.days_60 || r['31_60'] || r.days60 || 0);
            const d90     = parseFloat(r['61_90_days'] || r.days_90 || r['61_90'] || r.days90 || 0);
            const over90  = parseFloat(r['over_90_days'] || r.over_90 || r['90_plus'] || r.over90 || 0);
            const total   = parseFloat(r.total_due) || (current + d30 + d60 + d90 + over90);
            return (
              <tr key={i}>
                <td style={{ fontWeight: 600 }}>{r.name || r.supplier || r.contact_name || '—'}</td>
                <td className="text-right">{fmt(current)}</td>
                <td className="text-right">{fmt(d30)}</td>
                <td className="text-right">{fmt(d60)}</td>
                <td className="text-right">{fmt(d90)}</td>
                <td className="text-right" style={{ color: over90 ? '#ef4444' : '#94a3b8' }}>{fmt(over90)}</td>
                <td className="text-right" style={{ fontWeight: 700 }}>{fmt(total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/* ─── AR Ageing Details ───────────────────────────────── */
const ArAgeingDetailsView = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    accountingApi.getArAgeingDetails()
      .then(res => setData(res.data || res))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (error)   return <Err msg={error} />;
  if (!data)   return <Err msg="No data returned" />;

  const buckets = [
    { key: 'current', label: 'Current', title: 'Current' },
    { key: '1_30',    label: '1 - 30 days past due', title: '1 - 30 days past due' },
    { key: '31_60',   label: '31 - 60 days past due', title: '31 - 60 days past due' },
    { key: '61_90',   label: '61 - 90 days past due', title: '61 - 90 days past due' },
    { key: '>90',     label: '91 days and over past due', title: '91 days and over past due' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {buckets.map(b => {
        const rows = data[b.key] || [];
        const total = rows.reduce((s, r) => s + (parseFloat(r.due) || 0), 0);
        return (
          <div key={b.key} className="acc-card" style={{ padding: '1rem', border: '1px solid var(--acc-border,#e2e8f0)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <span style={{ color: '#10b981', fontSize: '0.9rem' }}>🔘</span>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--acc-text-main)' }}>{b.label}</h3>
            </div>
            <table className="acc-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Transaction Type</th>
                  <th>Invoice No.</th>
                  <th>Customer</th>
                  <th>Due Date</th>
                  <th className="text-right">Due</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={6} style={{ color: '#94a3b8', textAlign: 'center', padding: '1rem' }}>No invoices in this range</td></tr>
                ) : rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.transaction_date || r.date || '—'}</td>
                    <td>Invoice</td>
                    <td style={{ fontWeight: 600, color: 'var(--acc-primary)' }}>{r.invoice_no || r.ref_no || '—'}</td>
                    <td>{r.contact_name || '—'}</td>
                    <td>{r.due_date || '—'}</td>
                    <td className="text-right" style={{ fontWeight: 700 }}>{fmt(r.due)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#e2e8f0', fontWeight: 700, color: '#0f172a' }}>
                  <td colSpan={5}>Total for {b.title}</td>
                  <td className="text-right">{fmt(total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      })}
    </div>
  );
};

/* ─── AP Ageing Details ───────────────────────────────── */
const ApAgeingDetailsView = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    accountingApi.getApAgeingDetails()
      .then(res => setData(res.data || res))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (error)   return <Err msg={error} />;
  if (!data)   return <Err msg="No data returned" />;

  const buckets = [
    { key: 'current', label: 'Current', title: 'Current' },
    { key: '1_30',    label: '1 - 30 days past due', title: '1 - 30 days past due' },
    { key: '31_60',   label: '31 - 60 days past due', title: '31 - 60 days past due' },
    { key: '61_90',   label: '61 - 90 days past due', title: '61 - 90 days past due' },
    { key: '>90',     label: '91 days and over past due', title: '91 days and over past due' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {buckets.map(b => {
        const rows = data[b.key] || [];
        const total = rows.reduce((s, r) => s + (parseFloat(r.due) || 0), 0);
        return (
          <div key={b.key} className="acc-card" style={{ padding: '1rem', border: '1px solid var(--acc-border,#e2e8f0)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <span style={{ color: '#10b981', fontSize: '0.9rem' }}>🔘</span>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--acc-text-main)' }}>{b.label}</h3>
            </div>
            <table className="acc-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Transaction Type</th>
                  <th>Ref No.</th>
                  <th>Supplier</th>
                  <th>Due Date</th>
                  <th className="text-right">Due</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={6} style={{ color: '#94a3b8', textAlign: 'center', padding: '1rem' }}>No bills in this range</td></tr>
                ) : rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.transaction_date || r.date || '—'}</td>
                    <td>Purchase</td>
                    <td style={{ fontWeight: 600, color: 'var(--acc-primary)' }}>{r.ref_no || r.invoice_no || '—'}</td>
                    <td>{r.contact_name || '—'}</td>
                    <td>{r.due_date || '—'}</td>
                    <td className="text-right" style={{ fontWeight: 700 }}>{fmt(r.due)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#e2e8f0', fontWeight: 700, color: '#0f172a' }}>
                  <td colSpan={5}>Total for {b.title}</td>
                  <td className="text-right">{fmt(total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      })}
    </div>
  );
};

/* ─── Ledger Report View ───────────────────────────────── */
const LedgerReportView = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAcc, setSelectedAcc] = useState('');
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    accountingApi.getAccounts().then(res => {
      const arr = Array.isArray(res) ? res : (res.data || []);
      setAccounts(arr);
      if (arr.length > 0) {
        setSelectedAcc(arr[0].id);
        fetchLedger(arr[0].id);
      }
    });
  }, []);

  const fetchLedger = (accId) => {
    setLoading(true);
    accountingApi.getAccountLedger(accId)
      .then(res => setLedger(res.data || res || []))
      .catch(() => setLedger([]))
      .finally(() => setLoading(false));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <label style={{ fontWeight: 600 }}>Select Account:</label>
        <select
          value={selectedAcc}
          onChange={e => { setSelectedAcc(e.target.value); fetchLedger(e.target.value); }}
          style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', minWidth: '220px' }}
        >
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.gl_code || 'No Code'})</option>)}
        </select>
      </div>

      {loading ? <Loader /> : (
        <table className="acc-table">
          <thead>
            <tr><th>Date</th><th>Reference</th><th>Note</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr>
          </thead>
          <tbody>
            {ledger.length === 0 ? <Empty label="ledger" /> : ledger.map((row, i) => (
              <tr key={i}>
                <td>{row.date || '—'}</td>
                <td style={{ fontWeight: 600 }}>{row.ref_no || '—'}</td>
                <td>{row.note || '—'}</td>
                <td className="text-right" style={{ color: row.debit ? '#10b981' : '#94a3b8' }}>{row.debit ? fmt(row.debit) : '—'}</td>
                <td className="text-right" style={{ color: row.credit ? '#ef4444' : '#94a3b8' }}>{row.credit ? fmt(row.credit) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

/* ─── View map ────────────────────────────────────────── */
const VIEWS = {
  'trial-balance':       { title: 'Trial Balance',                              component: <TrialBalanceView /> },
  'balance-sheet':       { title: 'Balance Sheet',                              component: <BalanceSheetView /> },
  'ledger-report':        { title: 'Ledger Report',                              component: <LedgerReportView /> },
  'ar-ageing':            { title: 'Account Receivable Ageing Report (Summary)',  component: <ArAgeingView /> },
  'ap-ageing':            { title: 'Account Payable Ageing Report (Summary)',     component: <ApAgeingView /> },
  'ar-ageing-details':    { title: 'Account Receivable Ageing Details (Details)', component: <ArAgeingDetailsView /> },
  'ap-ageing-details':    { title: 'Account Payable Ageing Details (Details)',    component: <ApAgeingDetailsView /> },
};

/* ─── Report Card ─────────────────────────────────────── */
const ReportCard = ({ card, onView }) => (
  <div style={{ background: 'var(--acc-surface)', border: '1px solid var(--acc-border)', borderRadius: '8px', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', position: 'relative', overflow: 'hidden' }}>
    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg,#6366f1,#8b5cf6)' }} />
    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--acc-text-main)' }}>{card.title}</h3>
    <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--acc-text-muted)', lineHeight: 1.6, flexGrow: 1 }}>{card.description}</p>
    <div>
      <button
        onClick={() => onView(card.id)}
        style={{ background: '#5b21b6', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem 1.25rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
        onMouseEnter={e => e.target.style.background = '#4c1d95'}
        onMouseLeave={e => e.target.style.background = '#5b21b6'}
      >
        View Report
      </button>
    </div>
  </div>
);

/* ─── Main Component ──────────────────────────────────── */
const AccountingReports = () => {
  const [activeView, setActiveView] = useState(null);

  if (activeView) {
    const view = VIEWS[activeView];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              onClick={() => setActiveView(null)}
              className="acc-btn-secondary" style={{ padding: '0.4rem 0.9rem', fontSize: '0.875rem' }}
            >
              ← Back to Reports
            </button>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--acc-text-main)' }}>{view.title}</h2>
          </div>
          <button
            onClick={() => window.print()}
            className="acc-btn-secondary"
          >
            🖨 Print
          </button>
        </div>
        <div className="table-content-card" style={{ padding: '1.25rem' }}>
          {view.component}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px,1fr))', gap: '1.25rem' }}>
        {REPORT_CARDS.map(card => (
          <ReportCard key={card.id} card={card} onView={setActiveView} />
        ))}
      </div>
    </div>
  );
};

export default AccountingReports;
